import { inject, Injectable } from "@angular/core";

import { Observable } from "rxjs";

import { DateTime } from "luxon";

import { environment } from "@environments/environment";
import { LocalStorage } from "@shared/enums";
import { ADD_CODES, REMOVE_CODES } from "@shared/misc";
import {
  ActivityRecordResponse,
  ParsedSequence,
  RequestConfig,
  RequestState,
  StatusBadgeConfig,
} from "@shared/models";
import { ActivityItem } from "@shared/models/activity-record/activity-record-response";
import { ApiCacheService } from "@shared/services";

@Injectable({
  providedIn: "root",
})
export class ActivityRecordService {
  apiCacheService = inject(ApiCacheService);

  private readonly _baseUrl = environment.myTripUrl;
  private readonly _options = "includeSequences=true&includeCRR=false";

  /**
   * Get the activity records for a specific date.
   * @param date The date to get the activity records for.
   * @param forceRefresh Whether to force a refresh of the cache.
   * @returns An observable of {@link RequestState}<{@link ActivityRecordsResponse}>
   */
  getActivityRecord(
    date: string,
    forceRefresh: boolean,
    emulatedUser?: string,
  ): Observable<RequestState<ActivityRecordResponse>> {
    const config: RequestConfig = {
      cacheId: `seq-his-activityRecord/${date}`,
      cacheValidMinutes: 5,
      url: `${this._baseUrl}ActivityRecord/${date}?${this._options}`,
      arrayKey: "activityRecord",
      forceRefresh,
    };
    if (emulatedUser) {
      config.cacheId = `seq-his-activityRecord/${date}/${emulatedUser}`;
      config.url = `${this._baseUrl}ActivityRecord/${date}/${emulatedUser}?${this._options}`;
    }
    return this.apiCacheService.request<ActivityRecordResponse>(config);
  }

  /**
   * Parses the activity record response into an array of {@link ParsedSequence}.
   * @param activityRecord The {@link ActivityRecordResponse} to parse.
   * @returns An array of {@link ParsedSequence}.
   */
  parseActivityRecord(
    activityRecord: ActivityRecordResponse,
  ): ParsedSequence[] {
    this.handlePilotDetails(activityRecord);
    const validActivityItems =
      activityRecord.activityRecord.activityItems.filter((activityItem) => {
        return activityItem.linkedSequence && activityItem.positionCode;
      });
    const parsedSequences: ParsedSequence[] = validActivityItems.map(
      (activityItem: ActivityItem) => this.parseActivityItem(activityItem),
    );
    return parsedSequences;
  }

  private parseActivityItem(activityItem: ActivityItem): ParsedSequence {
    if (!activityItem.linkedSequence || !activityItem.positionCode) {
      return new ParsedSequence();
    } else {
      return new ParsedSequence({
        sequence: this.handleSequence(
          activityItem.linkedSequence.sequenceNumber,
        ),
        credit: this.handleCredit(activityItem.linkedSequence.sequenceTotal),
        date: this.handleDate(activityItem.originationDate),
        legs: this.handleLegs(activityItem),
        layovers: this.handleLayovers(activityItem),
        reasonCode: this.handleReason(activityItem).code,
        reasonDescription: this.handleReason(activityItem).description,
        state: this.handleState(activityItem),
        seat: activityItem.positionCode,
        originate: this.handleTimestamps(activityItem).originate,
        terminate: this.handleTimestamps(activityItem).terminate,
      });
    }
  }

  private handlePilotDetails(activityRecord: ActivityRecordResponse): void {
    const profile = activityRecord.activityRecord.profile;
    const storedProfile = localStorage.getItem(LocalStorage.Profile);
    if (!storedProfile) {
      localStorage.setItem(LocalStorage.Profile, JSON.stringify(profile));
    }
  }

  private handleSequence(sequence: string): string {
    return sequence.padStart(5, "0");
  }

  private handleCredit(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
  }

  private handleDate(date: string): string {
    const [sequenceDate, time] = date.split("T");
    const [year, month, day] = sequenceDate.split("-");
    const formattedDate = `${month}-${day}-${year.slice(2)}`;
    return formattedDate;
  }

  private handleLegs(activityItem: ActivityItem): string {
    let legsArray: number[] = [];
    activityItem.linkedSequence?.dutyPeriodSummaries.forEach(
      (dutyPeriodSummary) => {
        legsArray.push(dutyPeriodSummary.legs.length);
      },
    );
    return legsArray.join("-");
  }

  private handleLayovers(activityItem: ActivityItem): string {
    let layoversArray: string[] = [];
    activityItem.linkedSequence?.dutyPeriodSummaries.forEach((dutyPeriod) => {
      layoversArray.push(dutyPeriod.layoverStation);
    });
    layoversArray.pop();
    if (layoversArray.length > 0) {
      return layoversArray.join("-");
    } else {
      return "NONE";
    }
  }

  private handleReason(activityItem: ActivityItem): {
    code: string;
    description: string;
  } {
    const reasonCode =
      activityItem.addCode?.trim() || activityItem.removalCode?.trim() || "PBS";
    let reasonDescription = reasonCode
      ? ADD_CODES[reasonCode] || REMOVE_CODES[reasonCode] || ""
      : "";
    if (reasonCode === "PBS") {
      reasonDescription = "PBS Award";
    }
    return { code: reasonCode, description: reasonDescription };
  }

  private handleState(activityItem: ActivityItem): StatusBadgeConfig {
    const currentDate = DateTime.utc();
    const originationDate = DateTime.fromISO(activityItem.startDateTimeGmt);
    const terminationDate = DateTime.fromISO(activityItem.endDateTimeGmt);
    if (activityItem.removalCode?.trim()) {
      // Status: Removed
      return {
        class: "status removed",
        icon: "pi pi-times-circle",
        text: "Removed",
      };
    } else if (
      currentDate >= originationDate &&
      currentDate <= terminationDate
    ) {
      // Status: In Progress
      return {
        class: "status in-progress",
        icon: "pi pi-clock",
        text: "In Progress",
      };
    } else if (currentDate < originationDate) {
      return {
        class: "status scheduled",
        icon: "pi pi-arrow-circle-right",
        text: "Scheduled",
      };
    } else if (currentDate > terminationDate) {
      // Status: Completed
      return {
        class: "status completed",
        icon: "pi pi-check-circle",
        text: "Complete",
      };
    } else {
      // Status: Unknown
      return {
        class: "status unknown",
        icon: "pi pi-question-circle",
        text: "Unknown",
      };
    }
  }

  private handleTimestamps(activityItem: ActivityItem): {
    originate: { date: string; time: string };
    terminate: { date: string; time: string };
  } {
    const originateLocal = activityItem.startDateTimeLocal;
    const terminateLocal = activityItem.endDateTimeLocal;
    const originateGmt = activityItem.startDateTimeGmt;
    const terminateGmt = activityItem.endDateTimeGmt;
    return {
      originate: {
        date: this.handleDate(originateLocal),
        time: originateLocal.split("T")[1].slice(0, 5),
      },
      terminate: {
        date: this.handleDate(terminateLocal),
        time: terminateLocal.split("T")[1].slice(0, 5),
      },
    };
  }
}
